import { motion } from "framer-motion";
import { ArrowRight, CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { localizePath } from "@/utils/languageRoutes";
import { getOptimizedImageUrl } from "@/utils/imageUtils";
import { SectionHeading, EASE } from "./demo-shared";

interface DemoEventsProps {
  language: string;
}

const DemoEvents = ({ language }: DemoEventsProps) => {
  const isKo = language === "KO";

  const { data: events } = useQuery({
    queryKey: ["featuredEvents"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gallery_events")
        .select("*, gallery_images(*)")
        .eq("is_featured", true)
        .order("event_date", { ascending: false })
        .limit(3);
      if (error) throw error;
      return data;
    },
  });

  if (!events?.length) return null;

  return (
    <section className="relative bg-white py-24 md:py-32">
      <div
        aria-hidden
        className="absolute left-1/2 top-0 h-px w-[min(90%,72rem)] -translate-x-1/2 bg-gradient-to-r from-transparent via-slate-200 to-transparent"
      />
      <div className="container mx-auto max-w-6xl px-4">
        <SectionHeading
          no="05"
          eyebrow={isKo ? "활동" : "In the field"}
          title={isKo ? "두루의 전문성과 활동" : "Our Expertise in Action"}
          sub={
            isKo
              ? "현장에서 함께하는 두루의 네트워킹과 전문 활동을 만나보세요"
              : "Events, partnerships, and on-the-ground work across Korea and India"
          }
        />

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {events.map((event, index) => {
            const coverImage = event.gallery_images?.find((img: { is_cover: boolean }) => img.is_cover)?.image_url;
            const title = isKo ? event.title_ko || event.title_en : event.title_en;
            const description = isKo ? event.description_ko || event.description_en : event.description_en;
            const date = new Date(event.event_date).toLocaleDateString(isKo ? "ko-KR" : "en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
            return (
              <motion.article
                key={event.id}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [...EASE] }}
                className="group"
              >
                <div className="relative overflow-hidden rounded-3xl border border-slate-200/70 bg-slate-100">
                  <div className="aspect-[4/3] w-full overflow-hidden">
                    <img
                      src={coverImage ? getOptimizedImageUrl(coverImage, { width: 700, quality: 80 }) : "/placeholder.svg"}
                      alt={title}
                      className="h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-95"
                  />
                  <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur">
                    <CalendarDays className="h-3.5 w-3.5 text-primary" />
                    {date}
                  </span>
                  <h3 className="absolute inset-x-5 bottom-4 text-lg font-bold leading-snug text-white drop-shadow">
                    {title}
                  </h3>
                </div>
                {description && <p className="mt-4 line-clamp-2 text-sm leading-relaxed text-slate-600">{description}</p>}
              </motion.article>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-12 text-center"
        >
          <Link
            to={localizePath("/gallery", language)}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-primary-dark"
          >
            {isKo ? "더 많은 활동 보기" : "View more activities"}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default DemoEvents;
